import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { cnpj as cnpjValidator } from 'cpf-cnpj-validator';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { HttpService } from './http.service';

export interface CnpjAutocompleteData {
  razao_social?: string;
  nome_fantasia?: string;
  email?: string;
  ddd_telefone_1?: string;
  ddd_telefone_2?: string;
  ddd_fax?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  municipio?: string;
  uf?: string;
  bairro?: string;
  complemento?: string;
}

/**
 * Mapeamento entre campos do formulário Angular e campos retornados pela API de CNPJ.
 *
 * Cada chave é o nome de um control no FormGroup. O valor pode ser:
 * - string: nome direto do campo em CnpjAutocompleteData (ex: 'razao_social')
 * - função: recebe os dados da API e retorna o valor a ser aplicado,
 *   útil para combinar ou transformar campos (ex: formatar telefone com DDD)
 *
 * @example
 * const mapping: CnpjAutocompleteMapping = {
 *   name:  'razao_social',
 *   email: 'email',
 *   phone: (data) => data.ddd_telefone_1 ? `(${data.ddd_telefone_1})` : null,
 * };
 */
export type CnpjAutocompleteMapping = {
  [controlName: string]: keyof CnpjAutocompleteData | ((data: CnpjAutocompleteData) => any);
};

@Injectable({
  providedIn: 'root'
})
export class CnpjAutocompleteService {
  private cache = new Map<string, CnpjAutocompleteData>();

  constructor(private httpService: HttpService) {}

  watchForm(
    form: FormGroup,
    documentControlName: string,
    mapping: CnpjAutocompleteMapping
  ): Subscription {
    const documentControl = form.get(documentControlName);

    if (!documentControl) {
      return new Subscription();
    }

    return documentControl.valueChanges.pipe(
      debounceTime(300),
      map(value => this.onlyNumbers(value)),
      distinctUntilChanged(),
      filter(document => document.length === 14 && cnpjValidator.isValid(document)),
      switchMap(document => this.find(document)),
    ).subscribe(data => this.patchForm(form, mapping, data));
  }

  private find(cnpj: string): Observable<CnpjAutocompleteData> {
    if (this.cache.has(cnpj)) {
      return of(this.cache.get(cnpj) as CnpjAutocompleteData);
    }

    return this.httpService.get(`/v1/clients/autocomplete/cnpj/${cnpj}`).pipe(
      map(response => response?.data as CnpjAutocompleteData),
      filter(data => !!data),
      tap(data => this.cache.set(cnpj, data)),
      catchError(() => EMPTY),
    );
  }

  private patchForm(
    form: FormGroup,
    mapping: CnpjAutocompleteMapping,
    data: CnpjAutocompleteData
  ): void {
    Object.keys(mapping).forEach(controlName => {
      const control = form.get(controlName);
      const value = this.resolveValue(mapping[controlName], data);

      if (!control || this.wasEdited(control) || this.isEmpty(value)) {
        return;
      }

      control.patchValue(value);
    });
  }

  private resolveValue(
    source: keyof CnpjAutocompleteData | ((data: CnpjAutocompleteData) => any),
    data: CnpjAutocompleteData
  ): any {
    if (typeof source === 'function') {
      return source(data);
    }

    return data[source];
  }

  private wasEdited(control: AbstractControl): boolean {
    return control.dirty;
  }

  private isEmpty(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  private onlyNumbers(value: any): string {
    return (value || '').toString().replace(/\D/g, '');
  }
}
